import { Sections } from "./Mutators/Sections";
import { Attributes } from "./Mutators/Attributes";
import { Gallery } from "./Mutators/Gallery";
import { Variant } from "../../../Interfaces/Product";
import { ConfigHistory } from "./Mutators/ConfigHistory";
import { CampProps } from "./Mutators/CampProps";
import { ConfigOption } from "./ConfigOption";
import { HISTORY_MODE, HISTORY_MODES, SELECTORS } from "./Constants";
import { Dropdown } from "../../../Components/Form/Dropdown";

import { TConfigOptionName, TConfiguratorSelection, TValidOptionValues } from "./types";

export class Configurator {
    protected options: { [TConfigOptionName: string]: ConfigOption };
    protected sections: Sections;
    protected attributes: Attributes;
    protected gallery: Gallery;
    public variants: Variant[];
    private history: ConfigHistory;
    private campProps: CampProps;

    constructor(public node: HTMLElement) {
        this.node = node;
        this.sections = new Sections(this.node);
        if (!this.isRental) {
            this.attributes = new Attributes(this.node);
            this.campProps = new CampProps(this.node);
        }
        this.gallery = new Gallery(this.node);
        this.variants = JSON.parse(this.node.dataset.variants);
        this.initOptions();
        this.node.addEventListener("config", (e: CustomEvent) => this.handleChange(e));
        if (!this.isRental) {
            this.history = new ConfigHistory(this.selectedVariant, this.getAvailableOptionValues());
            if (HISTORY_MODE === HISTORY_MODES.PUSH) {
                this.history.onPopState = this.handleHistory.bind(this);
            }
        }
    }

    /**
     * initialize options
     * @private
     */
    protected initOptions(): void {
        this.options = [...this.node.querySelectorAll<Dropdown>("x-dropdown")]
            .map((dropdown) => new ConfigOption(dropdown, this.node))
            .reduce(
                (acc, cur) => {
                    acc[cur.name] = cur;
                    return acc;
                },
                {} as typeof this.options
            );
    }

    /**
     * handle change events
     * @param {CustomEvent} e
     * @private
     */
    protected handleChange(e: CustomEvent): void {
        const validValues = this.getValidVariantOptionValues(e.detail as ConfigOption);
        const initiator = e.detail.name;
        this.updateOptions(validValues, initiator);
        const variant = this.selectedVariant;
        this.mutate(variant);
        if (HISTORY_MODE === HISTORY_MODES.PUSH) {
            this.history.push(variant, this.getAvailableOptionValues());
        } else {
            this.history.replace(variant, this.getAvailableOptionValues());
        }
    }

    /**
     * handle history pop events
     * @param e
     * @private
     */
    private handleHistory(e: PopStateEvent) {
        const variant = e.state.variant as Variant;
        const validValues = e.state.validValues as TValidOptionValues;
        this.updateOptions(validValues, "");
        Object.keys(this.options).forEach((key: TConfigOptionName) => {
            this.options[key].silent = true;
            this.options[key].value = variant[key];
            this.options[key].silent = false;
        });
        this.mutate(variant);
    }

    /**
     * call mutators
     * @param v
     * @private
     */
    protected mutate(v: Variant) {
        this.attributes.update(v);
        this.gallery.update(v);
        this.sections.update(v).then(/* ignore promise */);
        if (!this.isRental) {
            this.campProps.update(v);
        }
    }

    /**
     * obtain values from dropdown elements that are currently not disabled
     * @private
     */
    protected getAvailableOptionValues(): TValidOptionValues {
        return Object.values(this.options).reduce(
            (acc, cur) => {
                cur.options.forEach((o) => {
                    if (!o.disabled) {
                        acc[cur.name].push(o.value);
                    }
                });
                return acc;
            },
            { option1: [], option2: [], option3: [] } as TValidOptionValues
        );
    }

    /**
     * obtain valid option values
     * @param option
     * @private
     */
    protected getValidVariantOptionValues(option: ConfigOption): TValidOptionValues {
        return this.variants
            .filter((variant) => {
                return variant.options.includes(option.value) && variant.available;
            })
            .reduce(
                (acc, cur) => {
                    acc.option1.push(cur.option1);
                    acc.option2.push(cur.option2);
                    acc.option3.push(cur.option3);
                    return acc;
                },
                { option1: [], option2: [], option3: [] } as TValidOptionValues
            );
    }

    /**
     * update options according to selection
     * @param validValues
     * @param initiator
     * @private
     */
    protected updateOptions(validValues: TValidOptionValues, initiator: string): void {
        Object.values(this.options)
            .filter((option) => {
                return option.index > this.options[initiator].index;
            })
            .forEach((option) => {
                option.validValues = validValues[option.name];
            });
    }

    /**
     * obtain selection
     */
    get selection(): TConfiguratorSelection {
        return Object.values(this.options).reduce(
            (acc, cur) => {
                acc[cur.name] = cur.value;
                return acc;
            },
            {} as typeof this.selection
        );
    }

    /**
     * obtain selected variant
     */
    get selectedVariant(): Variant {
        const selection = this.selection;
        return this.variants.find((variant) => {
            return (
                variant.option1 === (selection.option1 || null) &&
                variant.option2 === (selection.option2 || null) &&
                variant.option3 === (selection.option3 || null)
            );
        });
    }

    get isRental(): boolean {
        return this.node.dataset.isRental === "true";
    }
}

const configurator = document.querySelector<HTMLElement>(SELECTORS.CONFIGURATOR + ":not(.rental)");
let instance: Configurator = null;
if (configurator) {
    instance = new Configurator(configurator);
}

export default instance;
